/* eslint-disable ember/no-jquery */
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import Component from '@glimmer/component';
import { optional } from '@nullvoxpopuli/ember-composable-helpers';
import $ from 'jquery';

interface LoadMoreSignature {
  Args: {
    hasMorePages: boolean;
    useWindow?: boolean;
    action?: () => void;
    viewPortSelector?: string;
    useGetBoundingClientRect?: boolean;
  };
  Element: HTMLDivElement;
}

type JQueryViewport = {
  height: () => number;
  on: (event: string, handler: () => void) => void;
  off: (event: string) => void;
};

/**
 * @deprecated this component relies on JQuery and legacy patterns and should not be utilized going forward. use `InfinityLoader` instead with `ember-infinity`
 * */
export default class LoadMore extends Component<LoadMoreSignature> {
  #element!: HTMLDivElement;

  elementId = guidFor(this);

  @action
  inserted(element: HTMLDivElement): void {
    this.#element = element;

    const offset = 500;
    let viewPort: JQueryViewport;

    let isElementInViewport = function (el: HTMLElement) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const { top } = $(el).position();
      const viewPortHeight = viewPort.height();
      return top <= viewPortHeight + offset;
    };

    if (this.args.useWindow) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      viewPort = $(window);
      // eslint-disable-next-line @typescript-eslint/unbound-method
      isElementInViewport = this.isElementInViewport;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      viewPort = $(element).parents(this.args.viewPortSelector);
    }

    if (this.args.useGetBoundingClientRect) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      isElementInViewport = this.isElementInViewport;
    }

    const fireIfElementVisible = function (el: HTMLElement, callback: () => void) {
      return function () {
        if (isElementInViewport(el)) {
          callback();
        }
      };
    };

    const handler = fireIfElementVisible(element, () => {
      if (this.args.action) {
        this.args.action();
      }
    });

    const namespace = `loadMore_${this.elementId}`;
    viewPort.on(`DOMContentLoaded.${namespace} load.${namespace} resize.${namespace} scroll.${namespace}`, handler);
  }

  // Use getBoundingClientRect to check if element in viewPort
  isElementInViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    const bottomOffset = 500;

    return rect.top >= 0 && rect.bottom - bottomOffset <= (window.innerHeight || document.documentElement.clientHeight);
  }

  willDestroy(...args: Parameters<Component['willDestroy']>): void {
    super.willDestroy(...args);
    let viewPort: JQueryViewport;

    if (this.args.useWindow) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      viewPort = $(window);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      viewPort = $(this.#element).parents(this.args.viewPortSelector);
    }

    const namespace = `loadMore_${this.elementId}`;

    viewPort.off(`.${namespace}`);
  }

  <template>
    <div {{didInsert this.inserted}} id={{this.elementId}} ...attributes>
      {{#if @hasMorePages}}
        <div class="mx-auto text-center my-8 text-carbon-50">
          <button class="btn-reset" data-test-load-more {{on "click" (optional @action)}} type="button">
            <img
              alt=""
              src="/assets/images/form-loading.gif"
              width="20"
              class="align-middle mr-2"
              role="presentation"
            />
            Loading...
          </button>
        </div>
      {{/if}}
    </div>
  </template>
}
